/**
 * replay-utils.ts
 * ================
 * Pure utility functions for candle aggregation, normalization, and
 * replay display data transforms. No store imports — fully self-contained.
 *
 * Combined from aggregateCandles.js and replayTransforms.js reference files.
 */

// ─── Candle Interface ────────────────────────────────────────────────────────

export interface Candle {
  timestamp: number; // milliseconds
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  turnover?: number;
}

// ─── Timeframe Parsing ──────────────────────────────────────────────────────

/**
 * Legacy timeframe → minutes mapping.
 * Covers the standard keys used throughout the app.
 */
const LEGACY_TIMEFRAME_MINUTES: Record<string, number> = {
  '1m': 1,
  '5m': 5,
  '15m': 15,
  '30m': 30,
  '1h': 60,
  '4h': 240,
  '1d': 1440,
  d: 1440,
  '1w': 10080,
  '1mn': 43200,
  '1Mn': 43200,
  '1MN': 43200,
  '1mo': 43200,
  '1MO': 43200,
};

/**
 * Parse a timeframe string to minutes.
 *
 * Supports:
 *  - Legacy keys: '1m', '5m', '15m', '30m', '1h', '4h', '1d', '1w', '1Mn'
 *  - Dynamic parsing via regex: '<number>m' (minutes), '<number>h' (hours),
 *    '<number>d' (days), '<number>w' (weeks), '<number>mn/mo' (months)
 *
 * Returns `null` for unrecognised strings.
 */
export function parseTimeframeToMinutes(timeframe: string): number | null {
  if (typeof timeframe !== 'string') return null;

  const value = timeframe.trim();
  if (!value) return null;

  // Check legacy map first
  const legacy = LEGACY_TIMEFRAME_MINUTES[value];
  if (legacy !== undefined) return legacy;

  // Dynamic parsing: '2h' → 120, '30m' → 30, '3d' → 4320, '1w' → 10080, '2mn' → 86400
  const match = value.match(/^(\d+)\s*(mn|mo|m|h|d|w)$/i);
  if (match) {
    const num = parseInt(match[1], 10);
    const unit = match[2].toLowerCase();
    if (num <= 0) return null;
    if (num > 525600) return null; // max ~1 year in minutes

    switch (unit) {
      case 'mn':
      case 'mo':
        // 1 month = 30 days
        return num * 30 * 1440;
      case 'w':
        // 1 week = 7 days
        return num * 7 * 1440;
      case 'd':
        return num * 1440;
      case 'h':
        return num * 60;
      case 'm':
        return num;
    }
  }

  return null;
}

// ─── Timestamp Normalization ─────────────────────────────────────────────────

/**
 * Convert a raw epoch value to milliseconds.
 * If the value is > 0 and < 1e12, it's assumed to be in seconds and is
 * multiplied by 1000.
 */
export function normalizeTimestampMs(
  rawValue: number | null | undefined
): number | null {
  const value = Number(rawValue);
  if (!Number.isFinite(value)) return null;
  return value > 0 && value < 1e12 ? value * 1000 : value;
}

// ─── Candle Normalization ────────────────────────────────────────────────────

/**
 * Deduplicate candles by timestamp (keeps latest), validate OHLCV fields,
 * sort chronologically. Returns a clean Candle[] array.
 */
export function normalizeCandles(candles: any[]): Candle[] {
  if (!Array.isArray(candles)) return [];

  const byTimestamp = new Map<number, Candle>();

  candles.forEach((candle) => {
    const rawTimestamp = Number(candle?.timestamp);
    const timestamp =
      rawTimestamp > 0 && rawTimestamp < 1e12
        ? rawTimestamp * 1000
        : rawTimestamp;
    const open = Number(candle?.open);
    const high = Number(candle?.high);
    const low = Number(candle?.low);
    const close = Number(candle?.close);
    const volume = Number(candle?.volume ?? 0);

    if (
      !Number.isFinite(timestamp) ||
      !Number.isFinite(open) ||
      !Number.isFinite(high) ||
      !Number.isFinite(low) ||
      !Number.isFinite(close)
    ) {
      return;
    }

    byTimestamp.set(timestamp, {
      timestamp,
      open,
      high,
      low,
      close,
      volume: Number.isFinite(volume) ? volume : 0,
    });
  });

  return Array.from(byTimestamp.values()).sort(
    (a, b) => a.timestamp - b.timestamp
  );
}

// ─── Bucket / Granularity Utilities ──────────────────────────────────────────

/**
 * Map a timeframe string to its bucket granularity in seconds.
 * Uses `parseTimeframeToMinutes` internally.
 * Returns 60 (1 minute) as the safe fallback.
 */
export function resolveDisplayBucketGranularitySeconds(
  timeframe: string
): number {
  const timeframeMinutes = parseTimeframeToMinutes(timeframe);
  if (
    Number.isFinite(timeframeMinutes) &&
    timeframeMinutes !== null &&
    timeframeMinutes > 0
  ) {
    return timeframeMinutes * 60;
  }
  return 60;
}

/**
 * Compute the calendar-aligned bucket start timestamp for a given
 * millisecond timestamp and granularity (in seconds).
 *
 * Example with granularitySec = 300 (5m):
 *   1680000030000 → 1680000000000  (bucket starts at :00)
 *   1680000070000 → 1680000000000
 *   1680000120000 → 1680003000000  (next 5m bucket)
 */
export function resolveBucketTimestamp(
  timestamp: number,
  granularitySec: number
): number {
  const bucketSizeMs =
    Math.max(60, Math.floor(Number(granularitySec) || 60)) * 1000;
  return Math.floor(timestamp / bucketSizeMs) * bucketSizeMs;
}

// ─── Calendar-Aligned Bucket Start ───────────────────────────────────────────

/**
 * Compute the bucket start timestamp for a given candle timestamp and
 * timeframe size in minutes.
 *
 * Uses UTC to avoid DST issues.
 */
export function getBucketStart(timestampMs: number, tfMinutes: number): number {
  const tfMs = tfMinutes * 60 * 1000;
  return Math.floor(timestampMs / tfMs) * tfMs;
}

// ─── Future Trimming ─────────────────────────────────────────────────────────

/**
 * Remove any candles whose timestamp exceeds `maxTimestamp`.
 * Returns `{ nextDisplayData, trimmedCount }`.
 */
function trimFutureTrailingCandles(
  candles: Candle[],
  maxTimestamp: number
): { nextDisplayData: Candle[]; trimmedCount: number } {
  const current = Array.isArray(candles) ? candles : [];
  if (
    current.length === 0 ||
    !Number.isFinite(Number(maxTimestamp))
  ) {
    return { nextDisplayData: current, trimmedCount: 0 };
  }

  let lastValidIndex = current.length - 1;
  while (
    lastValidIndex >= 0 &&
    Number(current[lastValidIndex]?.timestamp) > maxTimestamp
  ) {
    lastValidIndex -= 1;
  }

  if (lastValidIndex === current.length - 1) {
    return { nextDisplayData: current, trimmedCount: 0 };
  }

  if (lastValidIndex < 0) {
    return { nextDisplayData: [], trimmedCount: current.length };
  }

  return {
    nextDisplayData: current.slice(0, lastValidIndex + 1),
    trimmedCount: current.length - (lastValidIndex + 1),
  };
}

// ─── Merge Tick into Display Series ──────────────────────────────────────────

/**
 * Merge a live tick into the display candle array.
 *
 * This is the CRITICAL function that prevents the "flat candle" bug:
 * - When the tick falls in the current (last) bucket, update H/L/C.
 * - When the tick falls in a new bucket (future), append a new candle.
 * - When the tick falls in a past bucket, ignore it.
 *
 * Also trims any future-trailing candles that may exist from a previous
 * state (e.g. after switching timeframes).
 *
 * @returns `{ nextDisplayData, mutationKind }`
 *   - `mutationKind` is 'append', 'replace_last', 'replace_all', or null
 */
export function mergeTickIntoDisplaySeries(
  displayData: Candle[],
  timestamp: number,
  price: number,
  granularitySec: number,
  tickVolume: number = 0,
): { nextDisplayData: Candle[]; mutationKind: string | null } {
  const bucketTimestamp = resolveBucketTimestamp(timestamp, granularitySec);
  const trimmed = trimFutureTrailingCandles(displayData, bucketTimestamp);
  const current = trimmed.nextDisplayData;
  const hadFutureTrailingCandles = trimmed.trimmedCount > 0;

  // Empty display → create first candle
  if (current.length === 0) {
    return {
      nextDisplayData: [
        {
          timestamp: bucketTimestamp,
          open: price,
          high: price,
          low: price,
          close: price,
          volume: tickVolume,
        },
      ],
      mutationKind: hadFutureTrailingCandles ? 'replace_all' : 'append',
    };
  }

  const last = current[current.length - 1];

  // Tick is in the past → ignore, unless we trimmed futures
  if (!last || bucketTimestamp < last.timestamp) {
    return {
      nextDisplayData: current,
      mutationKind: hadFutureTrailingCandles ? 'replace_all' : null,
    };
  }

  // Tick falls in the current (last) bucket → update H/L/C
  if (bucketTimestamp === last.timestamp) {
    return {
      nextDisplayData: [
        ...current.slice(0, -1),
        {
          ...last,
          high: Math.max(last.high, price),
          low: Math.min(last.low, price),
          close: price,
          volume: (Number(last.volume) || 0) + tickVolume,
        },
      ],
      mutationKind: hadFutureTrailingCandles ? 'replace_all' : 'replace_last',
    };
  }

  // Tick creates a new bucket → append
  return {
    nextDisplayData: [
      ...current,
      {
        timestamp: bucketTimestamp,
        open: price,
        high: price,
        low: price,
        close: price,
        volume: tickVolume,
      },
    ],
    mutationKind: hadFutureTrailingCandles ? 'replace_all' : 'append',
  };
}

// ─── Candle Aggregation ─────────────────────────────────────────────────────

/**
 * Aggregate 1-minute candles into higher timeframe candles.
 *
 * Uses calendar-aligned bucketing so the output matches natively-streamed
 * HTF data from exchanges like Binance and Deriv.
 *
 * @param masterData   - Full 1m OHLCV candle array
 * @param replayIndex  - Replay head (inclusive). Only candles 0..replayIndex are used.
 * @param timeframe    - Target timeframe key (e.g. '1m', '5m', '15m', '1h', '4h', '1d')
 * @returns Aggregated Candle[] sorted chronologically
 */
export function aggregateCandles(
  masterData: Candle[],
  replayIndex: number,
  timeframe: string
): Candle[] {
  if (!masterData || masterData.length === 0 || replayIndex < 0) {
    return [];
  }

  const tfMinutes = parseTimeframeToMinutes(timeframe);
  if (tfMinutes === null || tfMinutes <= 0) {
    // tfMinutes === 0 means tick-level — can't aggregate
    return [];
  }

  // Fast path: 1-minute is just a slice, no aggregation needed
  if (tfMinutes === 1) {
    return masterData.slice(0, replayIndex + 1);
  }

  // Aggregate using Map keyed by bucket start timestamp
  const buckets = new Map<number, Candle>();
  const end = Math.min(replayIndex, masterData.length - 1);

  for (let i = 0; i <= end; i++) {
    const candle = masterData[i];
    const bucketKey = getBucketStart(candle.timestamp, tfMinutes);

    const existing = buckets.get(bucketKey);
    if (existing) {
      if (candle.high > existing.high) existing.high = candle.high;
      if (candle.low < existing.low) existing.low = candle.low;
      existing.close = candle.close;
      existing.volume += candle.volume;
      if (existing.turnover !== undefined) {
        existing.turnover += candle.turnover || 0;
      }
    } else {
      buckets.set(bucketKey, {
        timestamp: bucketKey,
        open: candle.open,
        high: candle.high,
        low: candle.low,
        close: candle.close,
        volume: candle.volume,
        turnover: candle.turnover || 0,
      });
    }
  }

  return Array.from(buckets.values());
}

// ─── Two-Tier Display (Context + Replay) ─────────────────────────────────────

/**
 * Derive the display candle series from a two-tier data model:
 *  - contextCandles: pre-replay history at display-timeframe granularity
 *  - replayCandles:  1-minute replay-period data
 *
 * Both tiers are aggregated to the target display timeframe, then merged
 * with overlap trimming at the boundary.
 */
export function deriveTwoTierCandlesForView(
  contextCandles: Candle[],
  contextGranularitySeconds: number,
  replayCandles: Candle[],
  replayGranularitySeconds: number,
  replayIndex: number,
  timeframe: string,
): Candle[] {
  if (contextCandles.length === 0) {
    return deriveCandlesForViewStandalone(
      replayCandles,
      replayGranularitySeconds,
      replayIndex,
      timeframe,
    );
  }

  if (replayCandles.length === 0 || replayIndex < 0) {
    return deriveCandlesForViewStandalone(
      contextCandles,
      contextGranularitySeconds,
      contextCandles.length - 1,
      timeframe,
    );
  }

  const tfMinutes = parseTimeframeToMinutes(timeframe);
  if (!tfMinutes || tfMinutes <= 0) {
    return normalizeCandles(replayCandles.slice(0, replayIndex + 1));
  }

  // Aggregate context tier to display timeframe
  const contextMinutes = Math.max(contextGranularitySeconds / 60, 1);
  let contextDisplay: Candle[];
  if (tfMinutes <= contextMinutes) {
    contextDisplay = normalizeCandles(contextCandles);
  } else {
    contextDisplay = normalizeCandles(
      aggregateCandles(contextCandles, contextCandles.length - 1, timeframe),
    );
  }

  // Aggregate replay tier to display timeframe
  const replaySlice = replayCandles.slice(0, replayIndex + 1);
  const replayMinutes = Math.max(replayGranularitySeconds / 60, 1);
  let replayDisplay: Candle[];
  if (tfMinutes <= replayMinutes) {
    replayDisplay = normalizeCandles(replaySlice);
  } else {
    replayDisplay = normalizeCandles(
      aggregateCandles(replaySlice, replaySlice.length - 1, timeframe),
    );
  }

  if (contextDisplay.length === 0) return replayDisplay;
  if (replayDisplay.length === 0) return contextDisplay;

  // Trim context candles that overlap with the first replay bucket
  const firstReplayTs = replayDisplay[0].timestamp;
  const contextTrimmed = contextDisplay.filter(
    (c) => c.timestamp < firstReplayTs,
  );

  return [...contextTrimmed, ...replayDisplay];
}

/**
 * Standalone deriveCandlesForView (mirrors the one in chart-runtime.ts
 * but without importing from that module).
 */
function deriveCandlesForViewStandalone(
  source: Candle[],
  sourceGranularitySeconds: number,
  maxIndex: number,
  timeframe: string,
): Candle[] {
  if (source.length === 0) return [];

  const slice = source.slice(0, maxIndex + 1);
  const tfMinutes = parseTimeframeToMinutes(timeframe);
  const sourceMinutes = Math.max(sourceGranularitySeconds / 60, 1);

  if (!tfMinutes || tfMinutes <= sourceMinutes) {
    return normalizeCandles(slice);
  }

  return normalizeCandles(aggregateCandles(slice, slice.length - 1, timeframe));
}

// ─── Prepend Unique Candles (History Pagination) ────────────────────────────

/**
 * Prepend older candles to an existing series, skipping duplicates.
 * Only candles whose timestamp is strictly before the first existing
 * candle are prepended.
 *
 * @returns `{ nextSeries, prepended }`
 */
export function prependUniqueCandles(
  existing: Candle[],
  incoming: Candle[]
): { nextSeries: Candle[]; prepended: Candle[] } {
  const existingNormalized = Array.isArray(existing) ? existing : [];
  const incomingNormalized = normalizeCandles(incoming);

  if (incomingNormalized.length === 0) {
    return { nextSeries: existingNormalized, prepended: [] };
  }

  if (existingNormalized.length === 0) {
    return {
      nextSeries: incomingNormalized,
      prepended: incomingNormalized,
    };
  }

  const firstExistingTimestamp = Number(existingNormalized[0]?.timestamp);
  if (!Number.isFinite(firstExistingTimestamp)) {
    return {
      nextSeries: [...incomingNormalized, ...existingNormalized],
      prepended: incomingNormalized,
    };
  }

  const existingTimestamps = new Set(
    existingNormalized.map((c) => c.timestamp)
  );
  const prepended = incomingNormalized.filter(
    (candle) =>
      candle.timestamp < firstExistingTimestamp &&
      !existingTimestamps.has(candle.timestamp)
  );

  if (prepended.length === 0) {
    return { nextSeries: existingNormalized, prepended: [] };
  }

  return {
    nextSeries: [...prepended, ...existingNormalized],
    prepended,
  };
}

// ─── Append Unique Candles (Forward Buffer) ──────────────────────────────────

/**
 * Append newer candles to an existing series, skipping duplicates.
 * Only candles whose timestamp is strictly after the last existing
 * candle are appended. Mirrors `prependUniqueCandles` for the tail direction.
 *
 * @returns `{ nextSeries, appended }`
 */
export function appendUniqueCandles(
  existing: Candle[],
  incoming: Candle[]
): { nextSeries: Candle[]; appended: Candle[] } {
  const existingNormalized = Array.isArray(existing) ? existing : [];
  const incomingNormalized = normalizeCandles(incoming);

  if (incomingNormalized.length === 0) {
    return { nextSeries: existingNormalized, appended: [] };
  }

  if (existingNormalized.length === 0) {
    return {
      nextSeries: incomingNormalized,
      appended: incomingNormalized,
    };
  }

  const lastExistingTimestamp = Number(
    existingNormalized[existingNormalized.length - 1]?.timestamp
  );
  if (!Number.isFinite(lastExistingTimestamp)) {
    return {
      nextSeries: [...existingNormalized, ...incomingNormalized],
      appended: incomingNormalized,
    };
  }

  const existingTimestamps = new Set(
    existingNormalized.map((c) => c.timestamp)
  );
  const appended = incomingNormalized.filter(
    (candle) =>
      candle.timestamp > lastExistingTimestamp &&
      !existingTimestamps.has(candle.timestamp)
  );

  if (appended.length === 0) {
    return { nextSeries: existingNormalized, appended: [] };
  }

  return {
    nextSeries: [...existingNormalized, ...appended],
    appended,
  };
}

// ─── Whitespace Stabilisation Feed ──────────────────────────────────────────

/**
 * Build a full feed array for the chart consisting of:
 *  - `masterCandles.slice(0, position + 1)` — visible bars (real OHLCV)
 *  - `masterCandles.slice(position + 1)` mapped to NaN OHLCV — placeholder bars
 *    that keep the X-axis scale stable without rendering visible candles.
 *
 * This replicates TradingView's "whitespace hack" that prevents the chart from
 * jumping/resizing the timeline when new bars advance during replay.
 *
 * @param masterCandles   Full candle array held in the replay store.
 * @param position        Current replay head index (0-based, inclusive).
 */
export function buildWhitespaceFeed(
  masterCandles: Candle[],
  position: number
): { visible: Candle[]; whitespace: Candle[]; full: Candle[] } {
  const safePos = Math.max(0, Math.min(position, masterCandles.length - 1));
  const visible = masterCandles.slice(0, safePos + 1);
  const whitespace = masterCandles.slice(safePos + 1).map((c) => ({
    timestamp: c.timestamp,
    open: NaN,
    high: NaN,
    low: NaN,
    close: NaN,
    volume: NaN,
  }));
  return { visible, whitespace, full: [...visible, ...whitespace] };
}

// ─── Replay Loading Phase Label ──────────────────────────────────────────────

/**
 * Map a loadProgress value (0–100) to a human-readable phase label.
 * Phases match the three stages of useReplayBackfill:
 *   0        → preparing
 *   1–89     → loading 1-minute candle gaps from the CSV dataset
 *   90–99    → loading display-timeframe context candles
 *   100      → finalising / transitioning to ready
 */
export function getReplayLoadingMessage(progress: number): string {
  if (progress === 0) return "Connecting...";
  if (progress < 90) return "Fetching candles...";
  if (progress < 100) return "Loading context...";
  return "Preparing replay...";
}

// ─── Chart Mutation Token ────────────────────────────────────────────────────
